import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useProfileStore } from '../stores'
import { useI18n } from '../i18n'
import orellaLogo from '../assets/logos/orella_46A8F9_logo.png'
import orellaOIcon from '../assets/logos/orella_o_icon.png'
import { DIALOGUE_BUBBLE, DIALOGUE_PAD } from './agent/dialogueBubble'
import { isSectionComplete } from '../utils/profileCompletion'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

// Section definitions — structure only, labels come from locale
const SECTIONS_STRUCTURE = [
  { id: 'identification', path: '/profile/identification', labelKey: 'identificationLabel',  subtitleKey: 'identificationSubtitle',  required: true,  iconName: 'CreditCard' },
  { id: 'insurance',       path: '/profile/insurance',       labelKey: 'insuranceLabel',       subtitleKey: 'insuranceSubtitle',       required: true,  iconName: 'Shield' },
  { id: 'allergies',       path: '/profile/allergies',       labelKey: 'allergiesLabel',       subtitleKey: 'allergiesSubtitle',       required: true,  iconName: 'AlertTriangle' },
  { id: 'medications',     path: '/profile/medications',     labelKey: 'medicationsLabel',     subtitleKey: 'medicationsSubtitle',     required: true,  iconName: 'Pill' },
  { id: 'medical-history', path: '/profile/medical-history', labelKey: 'medicalHistoryLabel',  subtitleKey: 'medicalHistorySubtitle',  required: false, iconName: 'ClipboardList' },
  { id: 'family-history',  path: '/profile/family-history',  labelKey: 'familyHistoryLabel',   subtitleKey: 'familyHistorySubtitle',   required: false, iconName: 'Users' },
  { id: 'social-lifestyle',path: '/profile/social-lifestyle',labelKey: 'socialLifestyleLabel', subtitleKey: 'socialLifestyleSubtitle', required: false, iconName: 'Activity' },
]

// Section intro message keys (mapped by section id)
const SECTION_MESSAGE_KEYS = {
  'identification': 'sectionIntroIdentification',
  'insurance':      'sectionIntroInsurance',
  'allergies':      'sectionIntroAllergies',
  'medications':    'sectionIntroMedications',
  'medical-history':'sectionIntroMedicalHistory',
  'family-history': 'sectionIntroFamilyHistory',
  'social-lifestyle':'sectionIntroSocialLifestyle',
}

// ★★★ v242.157.0 — PROFILE-COMPLETENESS-FIFTH-COPY-1. A PRIVATE COPY OF
//   `isSectionComplete` LIVED HERE, and by the time it was measured it DISAGREED
//   with the canonical one in two places at once:
//
//     · identification — the copy asked for firstName + lastName + dob. The
//       canonical IDENTIFICATION_REQUIRED_FIELDS asks for EIGHT, including sex
//       and the full street address. So a patient with a name and a birthday and
//       no address got a green tick on the subpage and "incomplete" on
//       ProfileScreen, which reads the canonical one. Two screens, one patient,
//       opposite answers.
//     · social-lifestyle — the copy read the STORED `completed` flag, which
//       v242.150.0 (SOCIAL-COMPLETE-EARNED-1) deliberately stopped trusting
//       because pressing Save set it whatever the section contained. The copy
//       was still handing out the tick that ruling removed.
//
//   utils/profileCompletion.js says in its own header: "If you find yourself
//   writing a completeness check anywhere else in the app, that is the bug this
//   file was created to end." ★★ IT SAID SO WHILE TWO COPIES EXISTED. A rule
//   with a written owner and no arm is followed until someone is in a hurry
//   (§1dq(c), D388) — `scripts/check-profile-completeness-owner.mjs` is the arm.

// Get next section for "Save & Continue" hint
export function getNextSection(currentId) {
  const idx = SECTIONS_STRUCTURE.findIndex(s => s.id === currentId)
  if (idx === -1 || idx >= SECTIONS_STRUCTURE.length - 1) return null
  return SECTIONS_STRUCTURE[idx + 1]
}

// Typing dots component
function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1.5 h-5">
      <span className="w-2 h-2 rounded-full animate-[dotPulseSub_1s_ease-in-out_infinite]" style={{ background: '#A7D9FC' }} />
      <span className="w-2 h-2 rounded-full animate-[dotPulseSub_1s_ease-in-out_0.2s_infinite]" style={{ background: '#A7D9FC' }} />
      <span className="w-2 h-2 rounded-full animate-[dotPulseSub_1s_ease-in-out_0.4s_infinite]" style={{ background: '#A7D9FC' }} />
    </span>
  )
}

/**
 * ProfileSubpageLayout — shared wrapper for all profile sub-pages
 * 
 * Props:
 *   sectionId    — one of the SECTIONS ids
 *   onSave       — async function called when Save is pressed
 *   saving       — boolean, shows spinner in save bar
 *   canSave      — boolean, enables/disables save button (default true)
 *   busyLabel    — optional; the label beside the spinner while `saving` (default "Saving...").
 *                  P17 step 8: the card screens pass "Uploading…" while a card photo uploads.
 *   children     — form content
 */
export default function ProfileSubpageLayout({ sectionId, onSave, saving = false, canSave = true, busyLabel, children }) {
  const navigate = useNavigate()
  const { t } = useI18n()

  // Translated sections
  const SECTIONS = useMemo(() => SECTIONS_STRUCTURE.map(s => ({
    ...s,
    label: t('profileMain.' + s.labelKey),
    subtitle: t('profileMain.' + s.subtitleKey),
  })), [t])

  const SECTION_MESSAGES = useMemo(() => {
    const msgs = {}
    Object.entries(SECTION_MESSAGE_KEYS).forEach(([id, key]) => {
      msgs[id] = t('profileMain.' + key)
    })
    return msgs
  }, [t])
  const profile = useProfileStore(s => s.profile)
  const [showText, setShowText] = useState(false)
  const [showDots, setShowDots] = useState(true)

  const currentIndex = SECTIONS.findIndex(s => s.id === sectionId)
  const section = SECTIONS[currentIndex] || {}
  const nextSection = getNextSection(sectionId)
  const message = SECTION_MESSAGES[sectionId] || ''
  const isLastSection = currentIndex === SECTIONS.length - 1

  // Chat bubble animation — play once per section per session
  useEffect(() => {
    const hasPlayed = sessionStorage.getItem('profile_sub_' + sectionId)
    if (hasPlayed) {
      setShowDots(false)
      setShowText(true)
      return
    }
    const timer = setTimeout(() => {
      setShowDots(false)
      setShowText(true)
      sessionStorage.setItem('profile_sub_' + sectionId, 'true')
    }, 1000)
    return () => clearTimeout(timer)
  }, [sectionId])

  const handleSave = async () => {
    if (onSave) await onSave()
  }

  return (
    <div className="min-h-full bg-white flex flex-col relative">
      <style>{`
        @keyframes dotPulseSub { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1.2); } }
        @keyframes fadeInSub { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUpSub { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* ── Compact Header (matches 4D CompactHeader pattern) ── */}
      <div className="sticky-page-header px-4 pt-3 pb-0 bg-white flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <button onClick={() => navigate('/profile')} className="p-1.5 -ml-1.5 text-gray-500 active:scale-95 transition-transform">
              <ArrowLeft size={20} />
            </button>
            <img src={orellaLogo} alt="Orella" className="h-[18px] object-contain" />
          </div>
          <div className="flex items-center gap-2">
            {section.required && (
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#FEF3C7', color: '#D97706' }}>{t('profileMain.required')}</span>
            )}
            {!section.required && (
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#E9F8FF', color: '#46A8F9' }}>{t('profileMain.optional')}</span>
            )}
          </div>
        </div>

        {/* Breadcrumb — step counter removed; the progress dots below are the step indicator */}
        <div className="flex items-center gap-2 mt-2">
          <span className="text-[11px] text-gray-400">
            {t('profileMain.profileBreadcrumb')} › {section.label}
          </span>
        </div>

        {/* Step progress dots */}
        <div className="flex gap-1 mt-2 mb-2">
          {SECTIONS.map((s, i) => (
            <div
              key={s.id}
              className="h-[3px] flex-1 rounded-full transition-all"
              style={{
                background: isSectionComplete(s.id, profile)
                  ? '#66D170'
                  : i === currentIndex
                    ? '#46A8F9'
                    : '#E5E7EB'
              }}
            />
          ))}
        </div>

        <div className="h-px bg-gray-100" />
      </div>

      {/* ── Section Header (title + completion pill) — replaces the bordered identity card ── */}
      <div className="px-4 pt-3.5">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-[22px] font-extrabold leading-tight" style={{ color: '#15354F', letterSpacing: '-0.01em' }}>
            {section.label}
          </h1>
          {isSectionComplete(sectionId, profile) && (
            <span className="inline-flex items-center gap-1 flex-shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: 'rgba(102,209,112,0.18)', color: '#15834A' }}>
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#66D170"/><path d="M6 10.5L8.5 13L14 7.5" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {t('profileMain.complete')}
            </span>
          )}
        </div>
      </div>

      {/* ── Orella Chat Bubble (relocated below header; first-render animation preserved) ── */}
      <div className="px-4 pt-2.5">
        <div className="flex items-start gap-2.5">
          <img src={orellaOIcon} alt="Orella" className="w-9 h-9 rounded-xl flex-shrink-0 object-contain" />
          <div className={`flex-1 ${DIALOGUE_BUBBLE} ${DIALOGUE_PAD}`}>
            {showDots && !showText && <TypingDots />}
            {showText && (
              <p className="text-[15.5px] font-medium leading-relaxed animate-[fadeInSub_0.3s_ease]" style={{ color: '#15354F' }}>
                {message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Form Content (scrollable) ── */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-4">
        {children}
      </div>

      {/* ── Sticky Save Bar ── */}
      <div className="flex-shrink-0 bg-white border-t border-gray-100 px-4 py-3" style={{ boxShadow: '0 -4px 12px rgba(0,0,0,0.03)' }}>
        <button
          onClick={handleSave}
          disabled={saving || !canSave}
          className="w-full py-3.5 font-bold text-[14px] rounded-2xl transition-all active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-40"
          style={{ background: canSave ? '#15354F' : '#D1D5DB', color: 'white', boxShadow: canSave ? '0 4px 12px rgba(21,53,79,0.2)' : 'none' }}
        >
          {saving ? (
            <>
              <OrellaSpinner size={18} tone="inherit" />
              {busyLabel || t('profileMain.saving')}
            </>
          ) : (
            <>
              {t('profileMain.save')}
            </>
          )}
        </button>

      </div>
    </div>
  )
}

// ★ `isSectionComplete` is NOT re-exported. It was, and a re-export is a second
//   ADDRESS for one answer — the next importer would have reached the copy
//   without ever seeing the owner. Nothing imported it from here (measured
//   2026-09-06); anyone who needs it imports utils/profileCompletion directly.
export { SECTIONS_STRUCTURE, SECTION_MESSAGE_KEYS }
